import { useAtom, useAtomRefresh, useAtomValue } from '@effect/atom-react';
import { AsyncResult, Atom } from 'effect/reactivity';
import { Cause, Duration, Effect, Option } from 'effect';
import { useState } from 'react';
import { apiMutation, apiQuery, idleQuery, pollEvery } from './api-runtime';
import {
  documentInsightsFailureKind,
  documentInsightsSelection,
  loadSavedDocumentInsights,
  retrySavedDocumentInsights,
} from './document-insights';

// Loading insights may start a generation. Only its reads are retried: the
// POST that starts one is sent once, so a lost answer never starts another.

/** An audio's saved insights, polled every 2 seconds while they generate. */
export const savedDocumentInsightsAtom = Atom.family((audioId: string) =>
  apiQuery(loadSavedDocumentInsights(audioId, documentInsightsSelection), {
    staleTime: Duration.minutes(1),
  }).pipe(
    pollEvery((saved) =>
      Option.exists(saved, ({ status }) => status === 'pending')
        ? 2_000
        : false,
    ),
  ),
);

/** Retries a failed generation; the new record replaces the saved one. */
export const retryDocumentInsightsAtom = Atom.family((audioId: string) =>
  apiMutation((retryRequestId: string) =>
    retrySavedDocumentInsights({ audioId, retryRequestId }).pipe(
      Effect.tap((saved) =>
        Atom.set(savedDocumentInsightsAtom(audioId), saved),
      ),
    ),
  ),
);

const failureKindOf = <A, E>(result: AsyncResult.AsyncResult<A, E>) =>
  AsyncResult.isFailure(result)
    ? Option.match(Cause.findErrorOption(result.cause), {
        onNone: () => documentInsightsFailureKind(Cause.squash(result.cause)),
        onSome: documentInsightsFailureKind,
      })
    : null;

/**
 * The document's insights while `enabled`, and for as long as a generation it
 * started is still running.
 */
export function useSavedDocumentInsights({
  audioId,
  userId,
  enabled,
}: {
  audioId: string | undefined;
  userId: string | undefined;
  enabled: boolean;
}) {
  const ready = audioId !== undefined && userId !== undefined;
  const [generatingAudioId, setGeneratingAudioId] = useState<string>();
  const [retriedRequestId, setRetriedRequestId] = useState<string>();
  const insightsAtom =
    audioId !== undefined &&
    userId !== undefined &&
    (enabled || generatingAudioId === audioId)
      ? savedDocumentInsightsAtom(audioId)
      : idleQuery;
  const active = insightsAtom !== idleQuery;
  const result = useAtomValue(insightsAtom);
  const refresh = useAtomRefresh(insightsAtom);
  const [retryResult, retry] = useAtom(
    retryDocumentInsightsAtom(audioId ?? ''),
  );
  const saved = AsyncResult.getOrElse(result, () => undefined);

  const generating = saved?.status === 'pending' ? audioId : undefined;
  if (active && saved !== undefined && generating !== generatingAudioId)
    setGeneratingAudioId(generating);

  const retrying = retryResult.waiting;
  const failureKind =
    (retriedRequestId !== undefined && retriedRequestId === saved?.requestId
      ? failureKindOf(retryResult)
      : null) ?? failureKindOf(result);

  const generate = () => {
    if (!ready || retrying) return false;
    if (saved?.status !== 'failed') {
      setRetriedRequestId(undefined);
      refresh();
      return true;
    }
    setRetriedRequestId(saved.requestId);
    retry(saved.requestId);
    return true;
  };
  return {
    ready,
    saved,
    loading: result.waiting || retrying,
    failureKind,
    generate,
  };
}
